import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight, CircleAlert, CircleCheck, Download, FileSpreadsheet, FileUp, TriangleAlert, Upload, X } from 'lucide-react';
import { useGetCSVTemplateQuery, useImportEmployeesMutation } from '../store/slices/employeeSlice';
import { useDispatch } from 'react-redux';
import { addNotification } from '../store/slices/uiSlice';
import { IconTile, PageHeader, SectionCard, SpecGrid } from '../components/ui';
import { sbg, transition } from '../theme/sbgTokens';
import { MISSING, formatCount, formatDateTime, formatNumber, formatTime } from '../utils/format';
import ImportStepper from './import-parts/ImportStepper';
import PreviewTable from './import-parts/PreviewTable';
import type { RowFilter } from './import-parts/PreviewTable';
import { ImportFactsCard, TemplateColumnsCard } from './import-parts/ImportReference';
import {
  MAX_FILE_BYTES,
  applyServerIssues,
  buildPreview,
  joinWords,
  parseServerErrors,
} from './import-parts/csvPreview';
import type { CsvPreview, ServerIssues } from './import-parts/csvPreview';

type PreviewState =
  | { status: 'idle' }
  | { status: 'parsing' }
  | { status: 'error' }
  /** Over the server's limit: not read at all, so a huge file never freezes the tab. */
  | { status: 'oversize' }
  | { status: 'ready'; data: CsvPreview };

interface ImportFailure {
  title: string;
  body: string;
  /** The server's validation messages, rows renumbered to match the table. */
  messages: string[];
  /** Those messages indexed by row and email, to mark rows in the preview. */
  server: ServerIssues | null;
  toast: string;
  /** A next step shown on the alert. */
  action?: 'choose-again' | 'view-employees';
}

interface ImportSummary {
  fileName: string;
  created: number;
  updated: number;
  total: number;
  departments: number | null;
  at: Date;
}

/** Where focus goes after a transition unmounts the focused control. */
type FocusTarget = 'review' | 'browse' | 'results' | 'failure';

const MAX_LISTED_ERRORS = 8;
const REVIEW_TITLE_ID = 'import-review-title';
const RESULTS_TITLE_ID = 'import-results-title';
const COMMIT_NOTE_ID = 'import-commit-note';

/** 18.4 KB · 2.1 MB */
const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${formatNumber(bytes / (1024 * 1024), 1)} MB`
    : `${formatNumber(Math.max(bytes / 1024, 0.1), 1)} KB`;

/**
 * Chrome keeps a snapshot of a chosen file; once the file is saved again on
 * disk, reading (and uploading) that snapshot fails. Reading one byte first
 * tells "the file changed" apart from "the network dropped".
 */
const canStillRead = (file: File) =>
  new Promise<boolean>((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(true);
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, 1));
    } catch {
      resolve(true);
    }
  });

const STALE_FILE_FAILURE: ImportFailure = {
  title: 'This file changed after you chose it',
  body: 'Nothing was imported. Choose the file again so the review shows what will be imported.',
  messages: [],
  server: null,
  toast: 'Import stopped: the file changed after you chose it.',
  action: 'choose-again',
};

/**
 * Turns an RTK Query error from POST /employees/import-csv into what happened,
 * why, and what to do now. Validation failures carry `data.data.errors`.
 */
const describeImportFailure = (error: unknown): ImportFailure => {
  const err = (error ?? {}) as { status?: number | string; data?: { error?: unknown; data?: { errors?: unknown } } };
  const rawErrors = err.data?.data?.errors;
  const messages = Array.isArray(rawErrors) ? rawErrors.filter((item): item is string => typeof item === 'string') : [];

  if (messages.length > 0) {
    const count = messages.length;
    const server = parseServerErrors(messages);
    return {
      title: 'Nothing was imported',
      body: `The server found ${formatCount(count, 'problem')} in this file. Fix ${count === 1 ? 'it' : 'them'} in your spreadsheet, then choose the file again.`,
      messages: server.messages,
      server,
      toast: `Import failed: ${formatCount(count, 'problem')} to fix. Nothing was imported.`,
      action: 'choose-again',
    };
  }
  if (err.status === 'FETCH_ERROR') {
    // The request may have reached the server before the connection dropped.
    return {
      title: 'Couldn’t confirm the import',
      body: 'We lost the connection before the server answered. Check Employees, or run the import again: rows are matched by email, so nothing is duplicated.',
      messages: [],
      server: null,
      toast: 'Couldn’t confirm the import: the connection dropped.',
      action: 'view-employees',
    };
  }
  if (err.status === 401 || err.status === 403) {
    return {
      title: 'You can’t import employees',
      body: 'Only admins and HR can import. Sign in again, or ask an admin for access.',
      messages: [],
      server: null,
      toast: 'Import failed: you don’t have access.',
    };
  }
  if (err.status === 400) {
    const reason = typeof err.data?.error === 'string' ? `${err.data.error.replace(/\.?$/, '.')} ` : '';
    return {
      title: 'Nothing was imported',
      body: `${reason}Check the file, then choose it again.`,
      messages: [],
      server: null,
      toast: 'Import failed. Nothing was imported.',
      action: 'choose-again',
    };
  }
  return {
    title: 'The import didn’t finish',
    body: 'Some employees may already be saved. Running the import again is safe: rows are matched by email, so existing employees are updated, not duplicated.',
    messages: [],
    server: null,
    toast: 'Import didn’t finish. Run it again to complete it.',
    action: 'view-employees',
  };
};

const ImportData: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const browseRef = useRef<HTMLButtonElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<FocusTarget | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [failure, setFailure] = useState<ImportFailure | null>(null);
  const [lastImport, setLastImport] = useState<ImportSummary | null>(null);
  const [rowFilter, setRowFilter] = useState<RowFilter>('all');
  const [checkingFile, setCheckingFile] = useState(false);

  const dispatch = useDispatch();

  const { isLoading: isLoadingTemplate, error: templateError } = useGetCSVTemplateQuery();
  const [importEmployees, { isLoading: isImporting }] = useImportEmployeesMutation();
  const busy = isImporting || checkingFile;

  // Read the chosen file locally for the review table. The upload itself
  // always sends the original file; the server parses and validates it again.
  useEffect(() => {
    if (!selectedFile) {
      setPreview({ status: 'idle' });
      return undefined;
    }
    if (selectedFile.size > MAX_FILE_BYTES) {
      setPreview({ status: 'oversize' });
      return undefined;
    }
    let cancelled = false;
    setPreview({ status: 'parsing' });
    selectedFile
      .text()
      .then((text) => {
        if (!cancelled) setPreview({ status: 'ready', data: buildPreview(text) });
      })
      .catch(() => {
        if (!cancelled) setPreview({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedFile]);

  // Choosing, removing or importing a file swaps out the region that held
  // focus. Move focus to where the flow continues so keyboard and
  // screen-reader users keep their place.
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    const element =
      target === 'browse'
        ? browseRef.current
        : target === 'failure'
          ? failureRef.current
          : document.getElementById(target === 'review' ? REVIEW_TITLE_ID : RESULTS_TITLE_ID);
    if (!element) return;
    pendingFocus.current = null;
    if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1');
    element.focus();
  });

  const reviewed = useMemo(
    () => (preview.status === 'ready' ? applyServerIssues(preview.data, failure?.server ?? null) : null),
    [preview, failure],
  );

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const validateAndSetFile = (file: File) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setFileError(
        `“${file.name}” isn’t a CSV file. Export or download the sheet as .csv from your spreadsheet app, then choose that file.`,
      );
      if (selectedFile) pendingFocus.current = 'browse';
      setSelectedFile(null);
      setFailure(null);
      clearFileInput();
      return;
    }
    if (!selectedFile) pendingFocus.current = 'review';
    setSelectedFile(file);
    setFileError(null);
    setFailure(null);
    setRowFilter('all');
  };

  const handleUploadClick = () => {
    // Clear first: picking the same path again (after fixing the file) must
    // still fire a change event and read the new contents.
    clearFileInput();
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    pendingFocus.current = 'browse';
    setSelectedFile(null);
    setFileError(null);
    setFailure(null);
    clearFileInput();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      // Crossing a child element fires dragleave on the zone; only clear when the pointer really leaves it.
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (busy) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const reportFailure = (next: ImportFailure) => {
    setFailure(next);
    if (next.server && (next.server.byRow.size > 0 || next.server.byEmail.size > 0)) {
      setRowFilter('attention');
    }
    pendingFocus.current = 'failure';
    dispatch(
      addNotification({
        message: next.toast,
        type: 'error',
      })
    );
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setFileError('Choose a CSV file first.');
      return;
    }

    setCheckingFile(true);
    const readable = await canStillRead(selectedFile);
    setCheckingFile(false);
    if (!readable) {
      reportFailure(STALE_FILE_FAILURE);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const result = await importEmployees(formData).unwrap();

      const created = Number(result?.created) || 0;
      const updated = Number(result?.updated) || 0;
      const total = Number(result?.total) || created + updated;

      dispatch(
        addNotification({
          message: `Imported ${formatCount(total, 'employee')}: ${formatNumber(created)} created, ${formatNumber(updated)} updated.`,
          type: 'success',
        })
      );

      setLastImport({
        fileName: selectedFile.name,
        created,
        updated,
        total,
        departments: preview.status === 'ready' ? preview.data.departments : null,
        at: new Date(),
      });
      pendingFocus.current = 'results';
      setFailure(null);
      setSelectedFile(null);
      clearFileInput();
    } catch (error) {
      console.error('Import failed:', error);
      reportFailure(describeImportFailure(error));
    }
  };

  const handleDownloadTemplate = () => {
    // Keep in sync with backend generateCSVTemplate (csvParser.ts): new
    // policy-managed employees REQUIRE startDate; customAllotment=true rows
    // use their explicit pto/sick hours instead of the tenure policy.
    const csvContent = `name,email,role,department,startDate,customAllotment,ptoAllotment,sickTimeAllotment,wfhAllotment
John Doe,john.doe@company.com,Developer,Engineering,2026-07-01,,,,20
Jane Smith,jane.smith@company.com,Designer,Design,2026-07-01,true,200,56,20`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    dispatch(
      addNotification({
        message: 'Downloaded employee_template.csv.',
        type: 'success',
      })
    );
  };

  /* ---------------------------------------------------------------------- */
  /* Derived view state                                                      */
  /* ---------------------------------------------------------------------- */

  const templatePreparing = isLoadingTemplate && !templateError;
  const showResult = Boolean(lastImport && !selectedFile);
  const stepIndex = isImporting ? 2 : selectedFile ? 1 : lastImport ? 3 : 0;
  const checking = preview.status === 'parsing' || preview.status === 'idle';

  const rowCount = reviewed?.rows.length ?? 0;
  const counts = reviewed?.counts ?? { ready: 0, review: 0, error: 0 };
  const attentionCount = counts.review + counts.error;

  const importLabel = rowCount > 0 ? `Import ${formatCount(rowCount, 'row')}` : 'Import file';
  const importingLabel = rowCount > 0 ? `Importing ${formatCount(rowCount, 'row')}…` : 'Importing…';

  const fileMeta = (() => {
    if (preview.status === 'parsing') return 'Checking rows…';
    if (preview.status === 'oversize') return 'Over the 13 MB limit';
    if (preview.status === 'error') return 'Couldn’t read the file';
    if (!reviewed) return null;
    return [formatCount(reviewed.rows.length, 'row'), formatCount(reviewed.departments, 'department')].join(' · ');
  })();

  const reviewSummary = (() => {
    if (checking) return 'Checking rows…';
    if (preview.status === 'oversize') return 'Too large to check or import.';
    if (preview.status === 'error' || !reviewed) return 'Couldn’t read this file.';
    if (rowCount === 0) return 'No rows to review.';
    return `${formatCount(counts.ready, 'row')} ready · ${formatNumber(attentionCount)} ${attentionCount === 1 ? 'needs' : 'need'} attention`;
  })();

  /**
   * Why the file can't be imported as it stands. The server rejects the whole
   * file when any of these hold, so the Import button stays disabled and this
   * note says what to do instead.
   */
  const blockReason: { tone: 'neutral' | 'danger'; text: string } | null = (() => {
    if (!selectedFile || checking) return null;
    if (preview.status === 'oversize') {
      return {
        tone: 'danger',
        text: 'Too large to import. Split it into files under 13 MB and import each one.',
      };
    }
    if (preview.status === 'error' || !reviewed) {
      return { tone: 'danger', text: 'Your browser couldn’t read this file, so it can’t upload it either. Choose the file again.' };
    }
    if (rowCount === 0) {
      return { tone: 'neutral', text: 'There’s nothing to import: this file has no employee rows.' };
    }
    if (reviewed.missingColumns.length > 0) {
      return {
        tone: 'danger',
        text: `Every row fails without ${joinWords(reviewed.missingColumns)}. Fix the header row, then choose the file again.`,
      };
    }
    if (counts.error > 0) {
      const one = counts.error === 1;
      return {
        tone: 'danger',
        text: `${formatCount(counts.error, 'row')} ${one ? 'has an error' : 'have errors'}. Nothing is imported while any row fails: fix ${one ? 'it' : 'them'} in your spreadsheet, then choose the file again.`,
      };
    }
    return null;
  })();

  const canImport = Boolean(selectedFile) && !checking && !blockReason;

  const commitNote: { tone: 'neutral' | 'success' | 'warning' | 'danger'; text: string } = (() => {
    if (checking) return { tone: 'neutral', text: 'Checking the file…' };
    if (blockReason) return blockReason;
    if (counts.review > 0) {
      const one = counts.review === 1;
      return {
        tone: 'warning',
        text: `${formatCount(counts.review, 'row')} ${one ? 'needs' : 'need'} review: ${one ? 'it imports' : 'they import'} only if ${one ? 'that employee already exists' : 'those employees already exist'}.`,
      };
    }
    return {
      tone: 'success',
      text: `${rowCount === 1 ? 'The row is' : `All ${formatNumber(rowCount)} rows are`} ready. Matching emails update existing employees; the rest are added.`,
    };
  })();

  const noteTone = commitNote.tone === 'neutral' ? null : sbg.status[commitNote.tone];
  const NoteIcon = commitNote.tone === 'danger' ? CircleAlert : commitNote.tone === 'warning' ? TriangleAlert : CircleCheck;

  // One stable grid: the working card (upload, then review) always spans the
  // top; the reference cards always sit underneath. Nothing moves sideways
  // when a file is chosen, and the review table gets the full width.
  const areasWide = [showResult && '"results results"', '"main main"', '"columns facts"'].filter(Boolean).join(' ');
  const areasNarrow = [showResult && '"results"', '"main"', '"facts"', '"columns"'].filter(Boolean).join(' ');

  /* ---------------------------------------------------------------------- */
  /* Regions                                                                 */
  /* ---------------------------------------------------------------------- */

  const fileErrorLine = fileError && (
    <Box role="alert" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: sbg.status.danger.text, '& > svg': { flex: 'none', mt: '2px' } }}>
      <CircleAlert size={16} />
      <Typography variant="body2" component="p" sx={{ color: 'inherit' }}>
        {fileError}
      </Typography>
    </Box>
  );

  const uploadCard = (
    <SectionCard
      id="import-upload"
      title="Employee file"
      description="One row per employee, with the template’s column headers."
      bodySx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box
        onClick={handleUploadClick}
        sx={{
          minHeight: 200,
          display: 'grid',
          placeItems: 'center',
          alignContent: 'center',
          gap: 1.5,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          borderRadius: sbg.radius.lg,
          border: `1.5px dashed ${dragActive ? sbg.focus.ring : fileError ? sbg.status.danger.icon : sbg.border.strong}`,
          bgcolor: dragActive ? sbg.bg.selected : sbg.bg.surfaceSubtle,
          transition: transition('border-color', 'background-color'),
          '&:hover': { borderColor: sbg.focus.ring, bgcolor: sbg.bg.selected },
        }}
      >
        <IconTile size={40}>
          <FileUp />
        </IconTile>
        <Box>
          <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
            {dragActive ? 'Drop the file to check it' : 'Drop an employee CSV, or '}
            {!dragActive && (
              <Link
                ref={browseRef}
                component="button"
                type="button"
                aria-label="Browse for a CSV file"
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  handleUploadClick();
                }}
                sx={{ font: 'inherit', verticalAlign: 'baseline' }}
              >
                browse
              </Link>
            )}
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.5 }}>
            Needs name, email, role and department columns. CSV up to 13 MB.
          </Typography>
        </Box>
      </Box>
      {fileErrorLine}
    </SectionCard>
  );

  const fileStrip = selectedFile && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${sbg.border.subtle}`,
        bgcolor: dragActive ? sbg.bg.selected : sbg.bg.surfaceSubtle,
        outline: dragActive ? `1.5px dashed ${sbg.focus.ring}` : 'none',
        outlineOffset: -4,
        transition: transition('background-color'),
      }}
    >
      <IconTile size={32}>
        <FileSpreadsheet size={16} />
      </IconTile>
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <Typography variant="labelMd" component="p" noWrap title={selectedFile.name} sx={{ color: sbg.text.primary }}>
          {dragActive ? `Drop to replace ${selectedFile.name}` : selectedFile.name}
        </Typography>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
          {[formatFileSize(selectedFile.size), fileMeta].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button variant="outlined" size="small" onClick={handleUploadClick} disabled={busy}>
          Replace file
        </Button>
        <Tooltip title="Remove file">
          <span>
            <IconButton aria-label="Remove file" onClick={handleRemoveFile} disabled={busy}>
              <X size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  const failureAlert = failure && (
    <Alert
      key="failure"
      ref={failureRef}
      tabIndex={-1}
      severity="error"
      action={
        failure.action === 'choose-again' ? (
          <Button variant="text" size="small" onClick={handleUploadClick} disabled={busy}>
            Choose file again
          </Button>
        ) : failure.action === 'view-employees' ? (
          <Button component={RouterLink} to="/employees" variant="text" size="small">
            Check employees
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{failure.title}</AlertTitle>
      {failure.body}
      {failure.messages.length > 0 && (
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, display: 'grid', gap: 0.25 }}>
          {failure.messages.slice(0, MAX_LISTED_ERRORS).map((message, index) => (
            <li key={index}>{message}</li>
          ))}
        </Box>
      )}
      {failure.messages.length > MAX_LISTED_ERRORS && (
        <Typography variant="body2" component="p" sx={{ mt: 0.5, color: 'inherit' }}>
          And {formatCount(failure.messages.length - MAX_LISTED_ERRORS, 'more problem')}. Rows the server
          rejected are marked in the table.
        </Typography>
      )}
    </Alert>
  );

  const fileAlerts = selectedFile && [
    failureAlert,
    preview.status === 'oversize' && (
      <Alert key="size" severity="warning">
        <AlertTitle>This file is {formatFileSize(selectedFile.size)}</AlertTitle>
        The server accepts files up to 13 MB, and larger files aren’t checked here. Split it into smaller files, then
        choose one.
      </Alert>
    ),
    reviewed && reviewed.missingColumns.length > 0 && (
      <Alert key="missing" severity="error">
        <AlertTitle>The header row is missing {joinWords(reviewed.missingColumns)}</AlertTitle>
        Every row fails without {reviewed.missingColumns.length === 1 ? 'this column' : 'these columns'}. Header names
        are case-sensitive; download the template to start from the right headers.
      </Alert>
    ),
    reviewed && reviewed.ignoredColumns.length > 0 && (
      <Alert key="ignored" severity="info">
        {reviewed.ignoredColumns.length === 1 ? 'This column is' : 'These columns are'} ignored:{' '}
        {joinWords(reviewed.ignoredColumns)}. Only the template’s columns are imported.
      </Alert>
    ),
  ].filter(Boolean);

  const commitBar = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        {noteTone && (
          <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', flex: 'none', mt: '2px', color: noteTone.icon }}>
            <NoteIcon size={16} />
          </Box>
        )}
        <Typography
          id={COMMIT_NOTE_ID}
          variant="body2"
          component="p"
          sx={{ color: commitNote.tone === 'neutral' ? sbg.text.secondary : sbg.text.primary, maxWidth: '72ch' }}
        >
          {commitNote.text}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={handleImport}
        disabled={!canImport || busy}
        aria-busy={busy || undefined}
        aria-describedby={COMMIT_NOTE_ID}
        startIcon={busy ? <CircularProgress size={16} thickness={5} color="inherit" /> : <Upload />}
        sx={{ flexShrink: 0 }}
      >
        {busy ? importingLabel : importLabel}
      </Button>
    </Box>
  );

  const reviewCard = selectedFile && (
    <SectionCard
      id="import-review"
      title="Review rows"
      description={
        <Box component="span" role="status" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {reviewSummary}
        </Box>
      }
      flush
      footer={commitBar}
    >
      {fileStrip}
      {(fileAlerts || []).length > 0 && (
        <Box sx={{ display: 'grid', gap: 1.5, px: 2.5, py: 2, borderBottom: `1px solid ${sbg.border.subtle}` }}>{fileAlerts}</Box>
      )}
      {preview.status !== 'oversize' && (
        <PreviewTable
          key={`${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}`}
          embedded
          fileName={selectedFile.name}
          status={checking ? 'parsing' : preview.status === 'error' ? 'error' : 'ready'}
          rows={reviewed?.rows ?? []}
          hasHeader={reviewed?.hasHeader ?? true}
          attentionCount={attentionCount}
          filter={rowFilter}
          onFilterChange={setRowFilter}
          onChooseAnother={handleUploadClick}
        />
      )}
    </SectionCard>
  );

  return (
    <Box>
      <PageHeader
        overline="Tools"
        title="Import data"
        meta={['Employee records', 'CSV up to 13 MB', lastImport ? `Last import ${formatTime(lastImport.at)}` : null]}
        actions={
          <Button
            variant="outlined"
            onClick={handleDownloadTemplate}
            disabled={templatePreparing}
            aria-busy={templatePreparing || undefined}
            startIcon={templatePreparing ? <CircularProgress size={16} thickness={5} color="inherit" /> : <Download />}
          >
            {templatePreparing ? 'Preparing template…' : 'Download template'}
          </Button>
        }
      />

      <ImportStepper current={stepIndex} sx={{ mb: 3 }} />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".csv"
        aria-label="Employee CSV file"
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr)' },
          gridTemplateAreas: { xs: areasNarrow, lg: areasWide },
        }}
      >
        {showResult && lastImport && (
          <SectionCard
            id="import-results"
            title="Import complete"
            description={`${lastImport.fileName} · ${formatDateTime(lastImport.at)}`}
            sx={{ gridArea: 'results' }}
            footer={
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, width: '100%' }}>
                <Typography variant="body2" sx={{ color: sbg.text.secondary, flex: '1 1 240px' }}>
                  To import another file, drop it below.
                </Typography>
                <Button component={RouterLink} to="/employees" variant="contained" endIcon={<ArrowRight />}>
                  View employees
                </Button>
              </Box>
            }
          >
            <SpecGrid
              minColumnWidth={150}
              items={[
                {
                  label: 'Created',
                  value: formatNumber(lastImport.created),
                  caption: lastImport.created === 1 ? 'New employee' : 'New employees',
                },
                {
                  label: 'Updated',
                  value: formatNumber(lastImport.updated),
                  caption: 'Existing employees, matched by email',
                },
                {
                  label: 'Rows imported',
                  value: formatNumber(lastImport.total),
                  caption: 'Every row in the file',
                },
                {
                  label: 'Departments',
                  value: lastImport.departments === null ? MISSING : formatNumber(lastImport.departments),
                  caption: 'Named in the file; any new ones were added',
                },
              ]}
            />
          </SectionCard>
        )}

        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{ gridArea: 'main', minWidth: 0, display: 'flex', flexDirection: 'column', '& > *': { flex: 1 } }}
        >
          {selectedFile ? reviewCard : uploadCard}
        </Box>

        <Box sx={{ gridArea: 'facts', display: 'flex', flexDirection: 'column', minWidth: 0, '& > *': { flex: 1 } }}>
          <ImportFactsCard />
        </Box>

        <Box sx={{ gridArea: 'columns', minWidth: 0 }}>
          <TemplateColumnsCard />
        </Box>
      </Box>
    </Box>
  );
};

export default ImportData;
